import React, { useEffect, useState } from "react";
import API from "../services/api";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";

function Machines() {
  const [machines, setMachines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [periodData, setPeriodData] = useState({});
  const [historyData, setHistoryData] = useState({});
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientContactName, setClientContactName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [siiSealNumber, setSiiSealNumber] = useState("");
  const [siiNumber, setSiiNumber] = useState("");
  const [searchResults, setSearchResults] = useState({});
  const [previewCoords, setPreviewCoords] = useState({});
  const [addressInput, setAddressInput] = useState({});

  useEffect(() => {
    loadMachines();
    loadSummary();
  }, []);

  const loadMachines = async () => {
    const res = await API.get("/machines");
    setMachines(res.data);
  };

  const loadSummary = async () => {
    const res = await API.get("/machines/dashboard/summary");
    setSummary(res.data);
  };

  /** Primera coincidencia en Chile (Nominatim), para mapa tras crear máquina. */
  const geocodeFirstInChile = async (query) => {
    if (!query || !String(query).trim()) return null;
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&countrycodes=cl&addressdetails=1&limit=1&q=${encodeURIComponent(
        String(query).trim()
      )}`
    );
    const data = await response.json();
    return data && data.length ? data[0] : null;
  };

  const createMachine = async () => {
    if (!name || !location) {
      alert("Debe completar N° de máquina y ubicación");
      return;
    }

    const body = {
      name,
      location,
      client_name: clientName || null,
      client_contact_name: clientContactName || null,
      client_phone: clientPhone || null,
      client_email: clientEmail || null,
      serial_number: serialNumber || null,
      sii_seal_number: siiSealNumber || null,
      sii_number: siiNumber || null
    };

    const saved = { ...body };

    const res = await API.post("/machines", body);
    const newId = res.data?.id;

    if (newId && saved.location?.trim()) {
      try {
        const place = await geocodeFirstInChile(saved.location);
        if (place) {
          const lat = parseFloat(place.lat);
          const lon = parseFloat(place.lon);
          const city =
            place.address?.city ||
            place.address?.town ||
            place.address?.village ||
            place.address?.state ||
            "";
          await API.put(`/machines/${newId}`, {
            name: saved.name,
            location: place.display_name,
            latitude: lat,
            longitude: lon,
            city: city || null,
            client_name: saved.client_name,
            client_contact_name: saved.client_contact_name,
            client_phone: saved.client_phone,
            client_email: saved.client_email,
            serial_number: saved.serial_number,
            sii_seal_number: saved.sii_seal_number,
            sii_number: saved.sii_number
          });
        }
      } catch {
        /* sin coords: el usuario puede fijarlas en «Buscar dirección» */
      }
    }

    setName("");
    setLocation("");
    setClientName("");
    setClientContactName("");
    setClientPhone("");
    setClientEmail("");
    setSerialNumber("");
    setSiiSealNumber("");
    setSiiNumber("");
    loadMachines();
  };

  const loadPeriod = async (machineId) => {
    const res = await API.get(`/machines/${machineId}/period`);
    setPeriodData(prev => ({ ...prev, [machineId]: res.data }));
  };

  const loadHistory = async (machineId) => {
    const res = await API.get(`/machines/${machineId}/periods`);
    setHistoryData(prev => ({ ...prev, [machineId]: res.data }));
  };

  const updatePeriod = async (machineId) => {
    const data = periodData[machineId];

    await API.put(`/machines/${machineId}/period`, {
      counter_initial: parseInt(data.counter_initial),
      counter_final: parseInt(data.counter_final)
    });

    loadPeriod(machineId);
    loadSummary();
  };

  const formatCLP = value =>
    new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP"
    }).format(value || 0);

  const buildMachinePayload = (machine, overrides = {}) => ({
    name: machine.name,
    location: machine.location ?? "",
    latitude: machine.latitude ?? null,
    longitude: machine.longitude ?? null,
    city: machine.city ?? null,
    client_name: machine.client_name ?? null,
    client_contact_name: machine.client_contact_name ?? null,
    client_phone: machine.client_phone ?? null,
    client_email: machine.client_email ?? null,
    serial_number: machine.serial_number ?? null,
    sii_seal_number: machine.sii_seal_number ?? null,
    sii_number: machine.sii_number ?? null,
    ...overrides
  });

  const searchAddress = async (machineId, address) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&countrycodes=cl&addressdetails=1&q=${encodeURIComponent(address)}`
    );

    const data = await response.json();

    if (!data.length) {
      alert("Dirección no encontrada en Chile");
      return;
    }

    setSearchResults(prev => ({
      ...prev,
      [machineId]: data
    }));

  } catch (err) {
    alert("Error buscando dirección");
  }
};

  const selectAddress = async (machineId, place) => {
  const lat = parseFloat(place.lat);
  const lon = parseFloat(place.lon);

  const city =
    place.address.city ||
    place.address.town ||
    place.address.village ||
    place.address.state ||
    "";

  const m = machines.find(mm => mm.id === machineId);
  await API.put(`/machines/${machineId}`, buildMachinePayload(m, {
    location: place.display_name,
    latitude: lat,
    longitude: lon,
    city: city
  }));

  setPreviewCoords(prev => ({
    ...prev,
    [machineId]: { lat, lon }
  }));

  setSearchResults(prev => ({
    ...prev,
    [machineId]: []
  }));

  loadMachines();
};

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Máquinas</h1>
        <p className="page-subtitle">
          Datos de máquina, cliente, ubicación y resumen financiero.
        </p>
      </header>

      {summary && (
        <div
          className="card card--padded"
          style={{
            marginBottom: "1.25rem",
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)"
          }}
        >
          <h2 className="section-title">Resumen global</h2>
          <p style={{ margin: "0.35rem 0" }}>
            <strong>Ventas mes actual:</strong> {formatCLP(summary.monthTotal)}
          </p>
          {summary.prevMonthTotal != null && (
            <p style={{ margin: "0.35rem 0" }}>
              <strong>Ventas mes anterior:</strong> {formatCLP(summary.prevMonthTotal)}
            </p>
          )}
          {summary.yearTotal != null && (
            <p style={{ margin: "0.35rem 0" }}>
              <strong>Ventas año actual:</strong> {formatCLP(summary.yearTotal)}
            </p>
          )}
          {summary.variation != null && (
            <p style={{ margin: "0.35rem 0" }}>
              <strong>Variación mensual:</strong> {summary.variation}%{" "}
              {summary.variation >= 0 ? "↑" : "↓"}
            </p>
          )}
        </div>
      )}

      <div className="card card--padded" style={{ marginBottom: "1.5rem" }}>
        <h2 className="section-title">Crear nueva máquina</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end", marginBottom: "10px" }}>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
            N° de máquina *
            <input
              placeholder="Ej. 001"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ marginTop: "4px", minWidth: "120px" }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
            Ubicación *
            <input
              placeholder="Ej. Av. Providencia 1234, Santiago"
              value={location}
              onChange={e => setLocation(e.target.value)}
              style={{ marginTop: "4px", minWidth: "220px" }}
            />
            <span style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", maxWidth: "320px" }}>
              Se intenta ubicar en el mapa automáticamente. Usá una dirección clara en Chile.
            </span>
          </label>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end", marginBottom: "10px" }}>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
            N° de serie
            <input
              value={serialNumber}
              onChange={e => setSerialNumber(e.target.value)}
              style={{ marginTop: "4px", minWidth: "160px" }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
            N° Sello SII
            <input
              value={siiSealNumber}
              onChange={e => setSiiSealNumber(e.target.value)}
              style={{ marginTop: "4px", minWidth: "160px" }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
            N° SII
            <input
              value={siiNumber}
              onChange={e => setSiiNumber(e.target.value)}
              style={{ marginTop: "4px", minWidth: "160px" }}
            />
          </label>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end", marginBottom: "10px" }}>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
            Nombre cliente
            <input
              value={clientName}
              onChange={e => setClientName(e.target.value)}
              style={{ marginTop: "4px", minWidth: "180px" }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
            Nombre contacto cliente
            <input
              value={clientContactName}
              onChange={e => setClientContactName(e.target.value)}
              style={{ marginTop: "4px", minWidth: "180px" }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
            Teléfono
            <input
              type="tel"
              value={clientPhone}
              onChange={e => setClientPhone(e.target.value)}
              style={{ marginTop: "4px", minWidth: "140px" }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
            Email cliente
            <input
              type="email"
              value={clientEmail}
              onChange={e => setClientEmail(e.target.value)}
              style={{ marginTop: "4px", minWidth: "200px" }}
            />
          </label>
        </div>
        <button type="button" className="btn btn-primary" onClick={createMachine}>
          Crear
        </button>
      </div>

      {machines.map(machine => {
        const period = periodData[machine.id];
        const history = historyData[machine.id];

        return (
          <div key={machine.id} className="card card--padded" style={{ marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 16px", alignItems: "flex-end", marginBottom: "12px" }}>
              <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
                N° de máquina
                <input
                  type="text"
                  defaultValue={machine.name}
                  key={`name-${machine.id}-${machine.name}`}
                  onBlur={async e => {
                    await API.put(
                      `/machines/${machine.id}`,
                      buildMachinePayload(machine, { name: e.target.value })
                    );
                    loadMachines();
                  }}
                  style={{ marginTop: "4px", minWidth: "120px" }}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
                N° de serie
                <input
                  type="text"
                  defaultValue={machine.serial_number || ""}
                  key={`sn-${machine.id}-${machine.serial_number || ""}`}
                  onBlur={async e => {
                    await API.put(
                      `/machines/${machine.id}`,
                      buildMachinePayload(machine, { serial_number: e.target.value || null })
                    );
                    loadMachines();
                  }}
                  style={{ marginTop: "4px", minWidth: "140px" }}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
                N° Sello SII
                <input
                  type="text"
                  defaultValue={machine.sii_seal_number || ""}
                  key={`sss-${machine.id}-${machine.sii_seal_number || ""}`}
                  onBlur={async e => {
                    await API.put(
                      `/machines/${machine.id}`,
                      buildMachinePayload(machine, { sii_seal_number: e.target.value || null })
                    );
                    loadMachines();
                  }}
                  style={{ marginTop: "4px", minWidth: "140px" }}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
                N° SII
                <input
                  type="text"
                  defaultValue={machine.sii_number || ""}
                  key={`sii-${machine.id}-${machine.sii_number || ""}`}
                  onBlur={async e => {
                    await API.put(
                      `/machines/${machine.id}`,
                      buildMachinePayload(machine, { sii_number: e.target.value || null })
                    );
                    loadMachines();
                  }}
                  style={{ marginTop: "4px", minWidth: "140px" }}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
                Nombre cliente
                <input
                  type="text"
                  defaultValue={machine.client_name || ""}
                  key={`cn-${machine.id}-${machine.client_name || ""}`}
                  onBlur={async e => {
                    await API.put(
                      `/machines/${machine.id}`,
                      buildMachinePayload(machine, { client_name: e.target.value || null })
                    );
                    loadMachines();
                  }}
                  style={{ marginTop: "4px", minWidth: "160px" }}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
                Nombre contacto cliente
                <input
                  type="text"
                  defaultValue={machine.client_contact_name || ""}
                  key={`ccn-${machine.id}-${machine.client_contact_name || ""}`}
                  onBlur={async e => {
                    await API.put(
                      `/machines/${machine.id}`,
                      buildMachinePayload(machine, { client_contact_name: e.target.value || null })
                    );
                    loadMachines();
                  }}
                  style={{ marginTop: "4px", minWidth: "160px" }}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
                Teléfono
                <input
                  type="tel"
                  defaultValue={machine.client_phone || ""}
                  key={`cp-${machine.id}-${machine.client_phone || ""}`}
                  onBlur={async e => {
                    await API.put(
                      `/machines/${machine.id}`,
                      buildMachinePayload(machine, { client_phone: e.target.value || null })
                    );
                    loadMachines();
                  }}
                  style={{ marginTop: "4px", minWidth: "130px" }}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#475569" }}>
                Email cliente
                <input
                  type="email"
                  defaultValue={machine.client_email || ""}
                  key={`ce-${machine.id}-${machine.client_email || ""}`}
                  onBlur={async e => {
                    await API.put(
                      `/machines/${machine.id}`,
                      buildMachinePayload(machine, { client_email: e.target.value || null })
                    );
                    loadMachines();
                  }}
                  style={{ marginTop: "4px", minWidth: "200px" }}
                />
              </label>
            </div>
<div style={{ marginTop: "10px" }}>
  <strong>Dirección:</strong>

  <input
  type="text"
  placeholder="Buscar dirección en Chile"
  value={addressInput[machine.id] || ""}
  onChange={(e) =>
    setAddressInput(prev => ({
      ...prev,
      [machine.id]: e.target.value
    }))
  }
  style={{ marginLeft: "10px", width: "300px" }}
/>

 <button
  style={{ marginLeft: "10px" }}
  onClick={() =>
    searchAddress(machine.id, addressInput[machine.id])
  }
>
  🔍 Buscar
</button>

</div>
{searchResults[machine.id] &&
  searchResults[machine.id].map((result, index) => (
    <div
      key={index}
      style={{
        background: "#f1f5f9",
        padding: "8px",
        marginTop: "5px",
        cursor: "pointer",
        borderRadius: "5px"
      }}
      onClick={() => selectAddress(machine.id, result)}
    >
      {result.display_name}
    </div>
))}
{previewCoords[machine.id] && (
  <div style={{ marginTop: "10px" }}>
    <MapContainer
      center={[
        previewCoords[machine.id].lat,
        previewCoords[machine.id].lon
      ]}
      zoom={16}
      style={{ height: "200px", width: "100%" }}
    >
      <TileLayer
        attribution="© OpenStreetMap contributors"
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Marker
        position={[
          previewCoords[machine.id].lat,
          previewCoords[machine.id].lon
        ]}
      />
    </MapContainer>
  </div>
)}
<div style={{ marginTop: "10px" }}>
  <strong>Latitud:</strong>
  <input
    type="number"
    step="0.000001"
    defaultValue={machine.latitude || ""}
    onBlur={async (e) => {
      await API.put(
        `/machines/${machine.id}`,
        buildMachinePayload(machine, {
          latitude: e.target.value === "" ? null : e.target.value,
          longitude: machine.longitude
        })
      );
      loadMachines();
    }}
    style={{ marginLeft: "10px" }}
  />
</div>

<div style={{ marginTop: "10px" }}>
  <strong>Longitud:</strong>
  <input
    type="number"
    step="0.000001"
    defaultValue={machine.longitude || ""}
    onBlur={async (e) => {
      await API.put(
        `/machines/${machine.id}`,
        buildMachinePayload(machine, {
          latitude: machine.latitude,
          longitude: e.target.value === "" ? null : e.target.value
        })
      );
      loadMachines();
    }}
    style={{ marginLeft: "10px" }}
  />
</div>

            <button onClick={() => loadPeriod(machine.id)}>
              Cargar Período Actual
            </button>

            <button
              onClick={() => loadHistory(machine.id)}
              style={{ marginLeft: "10px" }}
            >
              Ver Histórico
            </button>

            {period && (
              <>
                <hr />
                <h3>Período Actual: {period.month}/{period.year}</h3>

                <p>
                  <strong>Contador Inicial:</strong>
                  <input
                    type="number"
                    value={period.counter_initial}
                    onChange={e =>
                      setPeriodData(prev => ({
                        ...prev,
                        [machine.id]: {
                          ...period,
                          counter_initial: e.target.value
                        }
                      }))
                    }
                  />
                </p>

                <p>
                  <strong>Contador Final:</strong>
                  <input
                    type="number"
                    value={period.counter_final}
                    onChange={e =>
                      setPeriodData(prev => ({
                        ...prev,
                        [machine.id]: {
                          ...period,
                          counter_final: e.target.value
                        }
                      }))
                    }
                  />
                </p>

                <p>
                  <strong>Diferencia Física:</strong>{" "}
                  {period.counter_final - period.counter_initial}
                </p>

                <p>
                  <strong>Ventas Sistema:</strong>{" "}
                  {formatCLP(period.total_sales_system)}
                </p>

                <button onClick={() => updatePeriod(machine.id)}>
                  Guardar
                </button>
              </>
            )}

            {history && (
              <>
                <hr />
                <h3>Histórico</h3>
                <table border="1" cellPadding="5">
                  <thead>
                    <tr>
                      <th>Mes</th>
                      <th>Inicial</th>
                      <th>Final</th>
                      <th>Diferencia</th>
                      <th>Cerrado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id}>
                        <td>{h.month}/{h.year}</td>
                        <td>{h.counter_initial}</td>
                        <td>{h.counter_final}</td>
                        <td>{h.counter_final - h.counter_initial}</td>
                        <td>{h.closed ? "Sí" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default Machines;